import React from "react";
import { HardDrive, Database } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * Single row in the backups table. Kept as its own component so hover /
 * confirm state doesn't re-render the whole list on every keystroke.
 */
export default function BackupRow({ row, onDownload, onRestore, onDelete }) {
  const kindLabel = {
    manual: "Manual",
    eod: "End of day",
    deploy: "On deploy",
    sync_src: "Sync source",
  }[row.kind] || row.kind;

  const kindStyle = {
    manual: "bg-brand/10 text-brand border-brand/30",
    eod: "bg-blue-50 text-blue-700 border-blue-200",
    deploy: "bg-amber-50 text-amber-800 border-amber-200",
    sync_src: "bg-slate-100 text-slate-700 border-slate-200",
  }[row.kind] || "bg-slate-100 text-slate-700 border-slate-200";

  const totalRows = Object.values(row.row_counts || {}).reduce((a, b) => a + b, 0);
  const sizeKB = Math.max(1, Math.round((row.size_bytes || 0) / 1024));
  const when = row.created_at ? new Date(row.created_at) : null;

  return (
    <tr
      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50"
      data-testid={`backup-row-${row.id}`}
    >
      <td className="px-3 py-3">
        <Badge className={`text-xs uppercase border ${kindStyle}`}>{kindLabel}</Badge>
      </td>
      <td className="px-3 py-3 text-sm text-slate-800 font-mono">
        {when ? when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}
      </td>
      <td className="px-3 py-3 text-sm text-slate-600 tabular-nums">
        {totalRows.toLocaleString()} rows
      </td>
      <td className="px-3 py-3 text-sm text-slate-600 tabular-nums">
        {sizeKB.toLocaleString()} KB
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2 text-xs">
          <span
            className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 ${
              row.local_status === "present"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-slate-200 bg-slate-50 text-slate-500 line-through"
            }`}
            title="Local pod-disk copy"
          >
            <HardDrive className="h-3 w-3" />
            local
          </span>
        </div>
      </td>
      <td className="px-3 py-3 text-xs text-slate-500 font-mono">{row.created_by || "—"}</td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDownload(row)}
            data-testid={`backup-download-${row.id}`}
          >
            Download
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRestore(row)}
            data-testid={`backup-restore-${row.id}`}
            disabled={row.local_status !== "present"}
            title={row.local_status !== "present" ? "Backup file no longer available on this pod" : ""}
          >
            <Database className="h-3.5 w-3.5 mr-1" />
            Restore
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(row)}
            data-testid={`backup-delete-${row.id}`}
            className="text-rose-700 hover:text-rose-800 hover:bg-rose-50"
          >
            Delete
          </Button>
        </div>
      </td>
    </tr>
  );
}
